import { useEffect, useRef, useState } from "react";
import "../../styles/rich-editor.css";
import EditorJS from "@editorjs/editorjs";
import { upload } from "../../lib/s3upload";
import { parseImageUrl, ImageSize, parseAssetUrl, randomStr } from "../../lib/helper";
import { FormInputProps } from "../../models/form.model";
import { Controller, FieldError } from "react-hook-form";
import { ClickAwayListener, FormHelperText, FormLabel } from "@mui/material";
import ResizeImageForm, { ResizeImageFormRef } from "../../containers/ResizeImageForm";

const Header = require("@editorjs/header");
// const Quote = require('@editorjs/quote'); todo remove later
const Image = require("@nnmax/editorjs-image");
const NestedList = require("@editorjs/nested-list");
const Checklist = require("@editorjs/checklist");
const LinkTool = require("@editorjs/link");
const Embed = require("@editorjs/embed");
const TableTool = require("@editorjs/table");
const AttachesTool = require("@editorjs/attaches");
const Alert = require("editorjs-alert");
const Underline = require("@editorjs/underline");
const ColorPlugin = require("editorjs-text-color-plugin");

export default function FormRichEditor(props: FormInputProps & { prefix: string; editorStyle?: React.CSSProperties }) {
  const { name, control, label, required, helperText, rules, editorStyle, prefix } = props;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value = {} }, fieldState: { error }, formState }) => (
        <div>
          {label && (
            <FormLabel className="margin-b-normal" required={required} error={!!error}>
              {label}
            </FormLabel>
          )}

          <Editor
            onChange={(v) => {
              onChange(v);
            }}
            error={error}
            style={editorStyle}
            defaultValue={value}
            prefix={prefix}
          />

          <FormHelperText className="margin-t-normal" error={!!error}>
            {error ? error.message : helperText || null}
          </FormHelperText>
        </div>
      )}
    />
  );
}

const Editor = (props: {
  prefix: string;
  onChange: (...event: any[]) => void;
  style?: React.CSSProperties;
  defaultValue?: any;
  error?: FieldError;
}) => {
  const { prefix, defaultValue, style, error } = props;
  const editor = useRef<EditorJS>();
  const [id, setId] = useState<string>();
  const resizeImageFormRef = useRef<ResizeImageFormRef>(null);

  useEffect(() => {
    const id = randomStr();
    setId(id);
  }, []);

  useEffect(() => {
    if (!id) return;
    editor.current = new EditorJS({
      placeholder: "Please edit your awesome content!",
      holder: id,
      data: defaultValue,
      tools: {
        header: {
          class: Header,
          inlineToolbar: true,
        },

        image: {
          class: Image,
          config: {
            uploader: {
              /**
               * Upload file to the server and return an uploaded image data
               * @param {File} file - file selected from the device or pasted by drag-n-drop
               * @return {Promise.<{success, file: {url}}>}
               */
              uploadByFile(file: File) {
                // your own uploading logic here
                return upload({ file, prefix }).then((res) => {
                  return {
                    success: 1,
                    file: {
                      url: parseImageUrl(res, ImageSize.normal),
                      style: "max-width: 100%",
                      // any other image data you want to store, such as width, height, color, extension, etc
                    },
                  };
                });
              },
            },
            actions: [
              {
                name: "resize_image",
                title: "Resize Image",
                action: () => {
                  if (!editor.current) return;
                  const currentBlockIndex = editor.current.blocks.getCurrentBlockIndex();
                  const currentBlock = editor.current.blocks.getBlockByIndex(currentBlockIndex);
                  if (!currentBlock) return;
                  const image = currentBlock.holder.querySelector("img") as HTMLImageElement | null;
                  const widthPercentage = getImageWidthPercentage(image);

                  window.webApp
                    .toggleConfirmDialog({
                      open: true,
                      title: "Resize Image",
                      element: <ResizeImageForm ref={resizeImageFormRef} defaultValues={{ width: widthPercentage }} />,
                      onOk: async () => {
                        if (!image || !editor.current || !resizeImageFormRef.current) return;
                        try {
                          const { width } = await resizeImageFormRef.current.submitForm();
                          image.style.width = `${width}%`;
                          editor.current.blocks.update(currentBlock.id, {
                            file: {
                              url: image.src,
                              style: image.style.cssText,
                            },
                          });
                        } catch (error) {
                          window.webApp.showMessage("error", "Resize image failed");
                        }
                      },
                    })
                    .catch(console.error);
                },
              },
            ],
          },
        },
        alert: Alert,
        Color: {
          class: ColorPlugin,
          config: {
            colorCollections: [
              "#EC7878",
              "#9C27B0",
              "#673AB7",
              "#3F51B5",
              "#0070FF",
              "#03A9F4",
              "#00BCD4",
              "#4CAF50",
              "#8BC34A",
              "#CDDC39",
              "#FFF",
            ],
            defaultColor: "#FF1300",
            type: "text",
            customPicker: true,
          },
        },
        list: {
          class: NestedList,
          inlineToolbar: true,
          config: {
            defaultStyle: "unordered",
          },
        },
        checklist: {
          class: Checklist,
          inlineToolbar: true,
        },
        linkTool: {
          class: LinkTool,
        },
        embed: {
          class: Embed,
          inlineToolbar: true,
          config: {
            services: {
              youtube: true,
              coub: true,
            },
          },
        },
        table: TableTool,
        underline: Underline,
        Marker: {
          class: ColorPlugin, // if load from CDN, please try: window.ColorPlugin
          config: {
            defaultColor: "#FFBF00",
            type: "marker",
            icon: `<svg fill="#000000" height="200px" width="200px" version="1.1" id="Icons" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 32 32" xml:space="preserve"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <path d="M17.6,6L6.9,16.7c-0.2,0.2-0.3,0.4-0.3,0.6L6,23.9c0,0.3,0.1,0.6,0.3,0.8C6.5,24.9,6.7,25,7,25c0,0,0.1,0,0.1,0l6.6-0.6 c0.2,0,0.5-0.1,0.6-0.3L25,13.4L17.6,6z"></path> <path d="M26.4,12l1.4-1.4c1.2-1.2,1.1-3.1-0.1-4.3l-3-3c-0.6-0.6-1.3-0.9-2.2-0.9c-0.8,0-1.6,0.3-2.2,0.9L19,4.6L26.4,12z"></path> </g> <g> <path d="M28,29H4c-0.6,0-1-0.4-1-1s0.4-1,1-1h24c0.6,0,1,0.4,1,1S28.6,29,28,29z"></path> </g> </g></svg>`,
          },
        },
        attaches: {
          class: AttachesTool,
          config: {
            uploader: {
              /**
               * Upload file to the server and return an uploaded image data
               * @param {File} file - file selected from the device or pasted by drag-n-drop
               * @return {Promise.<{success, file: {url}}>}
               */
              uploadByFile(file: File) {
                // your own uploading logic here
                return upload({ file, prefix }).then((res) => {
                  return {
                    success: 1,
                    file: {
                      url: parseAssetUrl(res),
                      // any other image data you want to store, such as width, height, color, extension, etc
                    },
                  };
                });
              },
            },
          },
        },
      },
    });
  }, [id]);

  return id ? (
    <ClickAwayListener
      onClickAway={async () => {
        var val = await editor.current?.save();
        props.onChange(val);
      }}
    >
      <div className="editor" id={id} style={{ ...style, borderColor: error ? "red" : "#c4c4c4" }}></div>
    </ClickAwayListener>
  ) : (
    <div className="editor"></div>
  );
};

function getImageWidthPercentage(image: HTMLImageElement | null) {
  if (!image) return 100;
  const imageWidthString = image.style.width.replace("%", "");
  const imageWidthNumber = Number.parseFloat(imageWidthString);
  if (Number.isNaN(imageWidthNumber)) {
    const imageParent = image.parentElement;
    if (!imageParent) return 100;
    const parentWidth = imageParent.clientWidth;
    const imageWidth = image.width;
    return Math.round((imageWidth / parentWidth) * 100);
  }
  return imageWidthNumber;
}